import { Fragment, useEffect, useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import { ButtonHeight } from '~/components/ui/Button';
import { CategoryTabs } from '~/components/ui/CategoryTabs';
import { CloseButton } from '~/components/ui/CloseButton';
import { PanelControls } from '~/components/ui/PanelControls';
import { ScrollContainer } from '~/components/ui/ScrollContainer';
import { ShopBalance } from '~/components/ui/ShopBalance';
import { ShopHead } from '~/components/ui/ShopHead';
import { ShopPreview } from '~/components/ui/ShopPreview';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction, InputKey, WalletType } from '~/shared';
import { ProductList } from '../../components/ProductList';
import { SectionHeading } from '../../components/SectionHeading';
import { useBuy, useShopPreview } from '../../hooks';
import {
	resetStore,
	setActiveItem,
	setActiveTab,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	allCategoriesIcon,
	getCategoriesFromItems,
	shopLogos,
} from './ProductShop.model';
import {
	StyledControlsContainer,
	StyledRightContainer,
	StyledShopContainer,
} from './ProductShop.styled';

const ProductShop = () => {
	const dispatch = useLayoutDispatch();
	const activeIndex = useLayoutSelector(
		(state) => state.productShop.activeIndex,
	);
	const shopType = useObservable(rage.productShop.getProductShopType());
	const maxIndex = useLayoutSelector((state) => state.productShop.maxIndex);
	const { handleBuy } = useBuy();
	const shopPreview = useShopPreview();
	const activeTab = useLayoutSelector((state) => state.productShop.activeTab);
	const items = useObservable(rage.productShop.getItems());
	const modalState = useObservable(rage.modal.getCurrentModal());

	const categories = useMemo(() => {
		return getCategoriesFromItems(items);
	}, [items]);

	const tabs = useMemo(
		() => [
			{ name: 'Все товары', icon: allCategoriesIcon },
			...categories.map((category) => ({
				name: category.name,
				icon: category.icon,
			})),
		],
		[categories],
	);
	const selectedTab = tabs[activeTab] ?? tabs[0];

	const leftControls = useMemo(
		() => [
			{
				text: 'Управление',
				keys: [
					InputKey.ArrowUp,
					InputKey.ArrowDown,
					InputKey.ArrowLeft,
					InputKey.ArrowRight,
				],
			},
			{
				text: 'Купить',
				keys: [InputKey.Enter],
				onClick: () => handleBuy(WalletType.Cash),
			},
		],
		[handleBuy],
	);

	const rightControls = [
		{
			text: 'Выйти',
			keys: ['Esc'],
			onClick: () => rage.productShop.exitMenu(),
		},
	];

	const handleSetActiveTab = (index: number) => {
		if (modalState) {
			return;
		}

		dispatch(setActiveTab(index));
		dispatch(setActiveItem(null));
	};

	const handlePrevTab = () => {
		handleSetActiveTab(activeTab === 0 ? 0 : activeTab - 1);
	};

	const handleNextTab = () => {
		handleSetActiveTab(
			activeTab === tabs.length - 1 ? activeTab : activeTab + 1,
		);
	};

	useEffect(() => {
		if (activeTab >= tabs.length && tabs.length > 0) {
			dispatch(setActiveTab(0));
			dispatch(setActiveItem(null));
		}
	}, [tabs.length, activeTab, dispatch]);

	useInputAction(InputAction.CommonMenuAccept, () =>
		handleBuy(WalletType.Cash),
	);

	useInputAction(InputAction.CommonMenuClose, () =>
		rage.productShop.exitMenu(),
	);

	useEffectOnce(() => {
		return () => {
			dispatch(resetStore());
		};
	});

	return (
		<Fragment>
			<StyledRightContainer>
				<ShopBalance />
				<CloseButton
					height={ButtonHeight.Height40}
					iconSize={20}
					onClick={() => rage.productShop.exitMenu()}
				/>
			</StyledRightContainer>
			<StyledShopContainer>
				<ShopHead img={shopLogos[shopType]} text={''} height={34} />
				<CategoryTabs
					tabs={tabs}
					activeIndex={activeTab}
					handlePrev={handlePrevTab}
					handleNext={handleNextTab}
					setIndex={handleSetActiveTab}
					style={{ marginTop: 20 }}
				/>
				<SectionHeading
					icon={selectedTab.icon}
					name={selectedTab.name}
					itemNumber={(activeIndex || 0) + 1}
					itemMax={maxIndex}
					style={{ marginTop: 30 }}
				/>
				<ScrollContainer height={598} style={{ marginTop: 20 }} showMask>
					<ProductList />
				</ScrollContainer>
			</StyledShopContainer>
			<StyledControlsContainer>
				<PanelControls controls={leftControls} align="left" />
				<PanelControls controls={rightControls} align="right" />
			</StyledControlsContainer>
			<ShopPreview {...shopPreview} />
		</Fragment>
	);
};

export { ProductShop };
